import { useCallback, useEffect, useMemo, useState } from "react";
import { fetchBookings, type Booking } from "@/lib/bookings";
import { listRows } from "@/lib/adminApi";
import type {
  Farm,
  Farmer,
  FarmerApplication,
  FarmerSettlement,
} from "@/lib/adminModules";
import type { Experience, Payment } from "@/lib/adminCommerce";
import { buildFarmPerformance, buildKpis } from "@/pages/admin/dashboard/dashboardData";
import DateRangeSelector, {
  type DateRangeKey,
} from "@/pages/admin/components/dashboard/DateRangeSelector";
import KpiGrid from "@/pages/admin/components/dashboard/KpiGrid";
import BookingsAnalytics from "@/pages/admin/components/dashboard/BookingsAnalytics";
import RevenueAnalytics from "@/pages/admin/components/dashboard/RevenueAnalytics";
import VisitorAnalytics from "@/pages/admin/components/dashboard/VisitorAnalytics";
import FarmPerformanceTable from "@/pages/admin/components/dashboard/FarmPerformanceTable";
import PendingActions from "@/pages/admin/components/dashboard/PendingActions";
import ActivityTimeline from "@/pages/admin/components/dashboard/ActivityTimeline";

function isoDaysAgo(days: number): string {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return `${date.getFullYear()}-${`${date.getMonth() + 1}`.padStart(2, "0")}-${`${date.getDate()}`.padStart(2, "0")}`;
}

function filterByRange(
  bookings: Booking[],
  range: DateRangeKey,
  customStart: string,
  customEnd: string,
): Booking[] {
  const now = new Date();
  const dayMs = 86400000;
  const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  let start: number | null = null;
  let end: number | null = null;

  if (range === "today") {
    start = startOfDay;
    end = startOfDay + dayMs;
  } else if (range === "yesterday") {
    start = startOfDay - dayMs;
    end = startOfDay;
  } else if (range === "7d") {
    start = startOfDay - 6 * dayMs;
  } else if (range === "30d") {
    start = startOfDay - 29 * dayMs;
  } else if (range === "month") {
    start = new Date(now.getFullYear(), now.getMonth(), 1).getTime();
  } else if (range === "custom") {
    if (customStart) start = new Date(customStart).getTime();
    if (customEnd) end = new Date(customEnd).getTime() + dayMs;
  }

  return bookings.filter((booking) => {
    const time = new Date(booking.created_at).getTime();
    if (start !== null && time < start) return false;
    if (end !== null && time >= end) return false;
    return true;
  });
}

const ROADMAP = [
  { phase: "Phase 1", title: "Application shell & bookings", status: "Live", done: true },
  { phase: "Phase 2", title: "Operations dashboard", status: "Live", done: true },
  { phase: "Phase 3", title: "Calendar, availability & check-ins", status: "Live", done: true },
  { phase: "Phase 4", title: "Farm network & people", status: "Live", done: true },
  { phase: "Phase 5", title: "Experiences & commerce", status: "Live", done: true },
  { phase: "Phase 6", title: "Content, support & administration", status: "Live", done: true },
];

export default function AdminOverview() {
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [farms, setFarms] = useState<Farm[]>([]);
  const [farmers, setFarmers] = useState<Farmer[]>([]);
  const [applications, setApplications] = useState<FarmerApplication[]>([]);
  const [settlements, setSettlements] = useState<FarmerSettlement[]>([]);
  const [experiences, setExperiences] = useState<Experience[]>([]);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [range, setRange] = useState<DateRangeKey>("30d");
  const [customStart, setCustomStart] = useState(isoDaysAgo(30));
  const [customEnd, setCustomEnd] = useState(isoDaysAgo(0));

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [bookingRows, farmRows, farmerRows, appRows, settlementRows, experienceRows, paymentRows] =
        await Promise.all([
          fetchBookings(),
          listRows<Farm>("farms", "name"),
          listRows<Farmer>("farmers", "name"),
          listRows<FarmerApplication>("farmer_applications", "submitted_at"),
          listRows<FarmerSettlement>("farmer_settlements", "final_settlement"),
          listRows<Experience>("experiences", "name"),
          listRows<Payment>("payments", "paid_at"),
        ]);
      setBookings(bookingRows);
      setFarms(farmRows);
      setFarmers(farmerRows);
      setApplications(appRows);
      setSettlements(settlementRows);
      setExperiences(experienceRows);
      setPayments(paymentRows);
    } catch (err) {
      setError(
        err instanceof Error && err.message
          ? err.message
          : "We couldn't load the latest numbers.",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const kpis = useMemo(() => {
    const today = isoDaysAgo(0);
    const pendingSettlements = settlements
      .filter((s) => ["pending", "ready", "processing"].includes(s.status))
      .reduce((sum, s) => sum + Number(s.final_settlement || 0), 0);
    const todayRevenue = payments
      .filter((p) => p.status === "paid" && p.paid_at === today)
      .reduce((sum, p) => sum + Number(p.amount || 0), 0);
    return buildKpis({
      bookings,
      activeFarms: farms.filter((f) => f.status === "active").length,
      activeExperiences: experiences.filter((e) => e.status === "published").length,
      pendingApprovals: applications.filter((a) => a.stage !== "approved" && a.stage !== "rejected").length,
      pendingSettlements,
      todayRevenue,
    });
  }, [bookings, farms, experiences, applications, settlements, payments]);

  const farmRows = useMemo(
    () => buildFarmPerformance(farms, farmers, settlements),
    [farms, farmers, settlements],
  );

  const rangedBookings = useMemo(
    () => filterByRange(bookings, range, customStart, customEnd),
    [bookings, range, customStart, customEnd],
  );

  return (
    <div>
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-7 md:mb-9">
        <div>
          <span className="text-[11px] font-semibold uppercase tracking-[0.22em] text-accent-700">
            Dashboard
          </span>
          <h1 className="font-heading text-3xl md:text-4xl text-foreground-950 font-semibold mt-2">
            Welcome back, Suresh
          </h1>
          <p className="text-sm md:text-base text-foreground-600 mt-2 max-w-2xl">
            Overview of Rythugaru Agri Tourism operations. Bookings, revenue, farms, experiences,
            approvals and settlements are live from your backend.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <DateRangeSelector
            value={range}
            onChange={setRange}
            customStart={customStart}
            customEnd={customEnd}
            onCustomChange={(start, end) => {
              setCustomStart(start);
              setCustomEnd(end);
            }}
          />
          <button
            type="button"
            onClick={load}
            className="inline-flex items-center gap-2 border border-background-300 text-foreground-800 text-sm font-semibold px-4 py-2.5 rounded-md hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className={`ri-refresh-line ${loading ? "animate-spin" : ""}`}></i>
            Refresh
          </button>
        </div>
      </div>

      {error ? (
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 bg-accent-100 border border-accent-300 rounded-lg px-4 py-3.5 mb-5">
          <p className="flex items-start gap-2 text-sm text-accent-900 flex-1">
            <i className="ri-error-warning-line text-lg"></i>
            <span>{error}</span>
          </p>
          <button
            type="button"
            onClick={load}
            className="inline-flex items-center gap-2 bg-accent-500 text-background-50 text-sm font-semibold px-4 py-2.5 rounded-md hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer self-start"
          >
            <i className="ri-refresh-line"></i>
            Retry
          </button>
        </div>
      ) : null}

      {loading ? (
        <div className="bg-background-50 border border-background-200 rounded-lg py-20 flex flex-col items-center gap-3">
          <i className="ri-loader-4-line text-3xl animate-spin text-primary-500"></i>
          <p className="text-sm text-foreground-600">Loading your numbers…</p>
        </div>
      ) : (
        <div className="space-y-5 md:space-y-6">
          <KpiGrid cards={kpis} />

          <div className="grid grid-cols-1 xl:grid-cols-3 gap-5 md:gap-6">
            <div className="xl:col-span-2">
              <BookingsAnalytics bookings={rangedBookings} />
            </div>
            <VisitorAnalytics />
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-3 gap-5 md:gap-6">
            <div className="xl:col-span-2">
              <RevenueAnalytics />
            </div>
            <ActivityTimeline />
          </div>

          <FarmPerformanceTable rows={farmRows} />

          <PendingActions />

          <section className="bg-background-50 border border-background-200 rounded-lg p-5 md:p-6">
            <h2 className="font-heading text-xl text-foreground-950 font-semibold">
              Platform roadmap
            </h2>
            <p className="text-sm text-foreground-600 mt-1">
              The admin portal is being built in phases. Here's where we are.
            </p>
            <ol className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3">
              {ROADMAP.map((step) => (
                <li key={step.phase} className="flex items-center gap-4">
                  <span
                    className={`w-9 h-9 flex items-center justify-center rounded-full shrink-0 ${
                      step.done
                        ? "bg-primary-500 text-background-50"
                        : "bg-background-100 text-foreground-500"
                    }`}
                  >
                    <i className={step.done ? "ri-check-line" : "ri-time-line"}></i>
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-foreground-950">
                      {step.phase} · {step.title}
                    </p>
                  </div>
                  <span
                    className={`text-[11px] font-semibold px-3 py-1 rounded-full border whitespace-nowrap ${
                      step.done
                        ? "bg-secondary-100 text-secondary-800 border-secondary-200"
                        : "bg-background-100 text-foreground-500 border-background-200"
                    }`}
                  >
                    {step.status}
                  </span>
                </li>
              ))}
            </ol>
          </section>
        </div>
      )}
    </div>
  );
}